import cn from 'clsx';
import dayjs from 'dayjs';
import React, { RefObject, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { ApiFilterOperation, TableField, usePrompts } from '@eas/common-web';
import { TableFieldProps } from '@eas/common-web/dist/types/components/table-field/table-field-types';
import { useMUAssignmentList } from '@modules/mobile-unit/mobile-unit-api';
import { MobileUnitAssignmentValidityTableCell } from '@composite/mobile-unit-assignment-collection/mobile-unit-assignment-columns';
import { DayPicker } from '@components/pickers/day-picker';
import { MonthPicker } from '@components/pickers/month-picker';
import { WeekPicker } from '@components/pickers/week-picker';
import { MobileUnitAssignment, ZsdUser } from '@models';
import { CalendarDimensions } from '@enums';
import { CalendarHandle } from '../calendar/calendar-types';
import { getScaleLabel } from '../calendar/utils';
import { PlannerContext } from '../planner-context';
import { UnitMenuProps } from './unit-menu';

const useStyles = makeStyles(() => ({
  group: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  button: {
    marginRight: '8px',
  },
  outlineButton: {
    borderColor: '#ccc',
  },
}));

const useColumns = (): TableFieldProps<MobileUnitAssignment>['columns'] => [
  {
    datakey: 'validFrom',
    name: 'Platnosť',
    width: 350,
    CellComponent: MobileUnitAssignmentValidityTableCell,
  },
  {
    datakey: 'leadTechnician',
    name: 'Vedúci',
    width: 230,
    CellComponent: ({ value }) => (
      <>
        {value?.lastName ?? ''} {value?.firstName ?? ''}
      </>
    ),
  },
  {
    datakey: 'technicians',
    name: 'Technici',
    width: 500,
    CellComponent: ({ value }) => (
      <>
        {(value as ZsdUser[])
          .map((t) => `${t?.lastName ?? ''} ${t?.firstName ?? ''}`)
          .join(', ')}
      </>
    ),
  },
];

export const useBrowseUnitAssignmentsDialog = (unit: UnitMenuProps['unit']) => {
  const plannerContext = useContext(PlannerContext);

  usePrompts(
    [
      {
        key: `UNIT_SHOW_ASSIGNMENT_DIALOG_${unit.id}`,
        dialogTitle: `Posádka ${unit.name}`,
        dialogText: (
          <UnitAssignmentDialog
            unit={unit}
            calendarRef={plannerContext.calendarRef}
          />
        ),
        dialogWidth: 1000,
        dialogShowClose: true,
        dialogShowConfirm: false,
      },
    ],
    [unit.id]
  );
  return {
    unitAssigmentDialogKey: `UNIT_SHOW_ASSIGNMENT_DIALOG_${unit.id}`,
  };
};

function UnitAssignmentDialog({
  unit,
  calendarRef,
}: {
  unit: UnitMenuProps['unit'];
  calendarRef: RefObject<CalendarHandle>;
}) {
  const classes = useStyles();

  const columns = useColumns();

  const settings = calendarRef?.current?.settings ?? {
    dimension: CalendarDimensions.DAY,
  };

  const { dimension = CalendarDimensions.DAY } = settings ?? {};
  const { currentDate = dayjs(new Date()).unix() } = calendarRef?.current ?? {};

  const [startDate, setStartDate] = useState(currentDate);

  const validFrom = dayjs.unix(startDate).startOf('day');
  const validTo = validFrom.add(1, dimension);

  const { result } = useMUAssignmentList({
    sort: [
      {
        field: 'validFrom',
        order: 'ASC',
        type: 'FIELD',
      },
    ],
    filters: [
      {
        field: 'mobileUnit.id',
        operation: ApiFilterOperation.EQ,
        value: unit.id,
      },

      {
        field: 'validFrom',
        operation: ApiFilterOperation.GTE,
        value: validFrom.format('YYYY-MM-DD'),
      },
      {
        field: 'validTo',
        operation: ApiFilterOperation.LTE,
        value: validTo.format('YYYY-MM-DD'),
      },
    ],
  });

  const handlePrevious = () => {
    setStartDate((prevDate) =>
      dayjs.unix(prevDate).subtract(1, dimension).unix()
    );
  };

  const handleNext = () => {
    setStartDate((prevDate) => dayjs.unix(prevDate).add(1, dimension).unix());
  };

  const handleNow = () => {
    const newDate = dayjs().startOf(dimension).unix();
    setStartDate(newDate);
  };

  return (
    <>
      <div>
        <div className={classes.group}>
          <Button
            variant="outlined"
            className={cn(classes.button, classes.outlineButton)}
            onClick={handleNow}
          >
            {dimension === CalendarDimensions.DAY && (
              <FormattedMessage
                id="ZSD__CALENDAR__TODAY"
                defaultMessage="Dnešný deň"
              />
            )}
            {dimension === CalendarDimensions.WEEK && (
              <FormattedMessage
                id="ZSD__CALENDAR__THIS_WEEK"
                defaultMessage="Aktuálny týždeň"
              />
            )}
            {dimension === CalendarDimensions.MONTH && (
              <FormattedMessage
                id="ZSD__CALENDAR__THIS_MONTH"
                defaultMessage="Aktuálny mesiac"
              />
            )}
          </Button>
          <IconButton size="small" onClick={handlePrevious}>
            <KeyboardArrowLeftIcon />
          </IconButton>

          {dimension === CalendarDimensions.DAY && (
            <DayPicker
              labelMapper={(d) => getScaleLabel(d, settings, [])}
              currentDate={startDate}
              onChange={(value) => {
                setStartDate(value);
              }}
            />
          )}
          {dimension === CalendarDimensions.WEEK && (
            <WeekPicker
              labelMapper={(d) => getScaleLabel(d, settings, [])}
              currentDate={startDate}
              onChange={(value) => {
                setStartDate(value);
              }}
            />
          )}
          {dimension === CalendarDimensions.MONTH && (
            <MonthPicker
              labelMapper={(d) => getScaleLabel(d, settings, [])}
              currentDate={startDate}
              onChange={(value) => {
                setStartDate(value);
              }}
            />
          )}

          <IconButton size="small" onClick={handleNext}>
            <KeyboardArrowRightIcon />
          </IconButton>
        </div>
      </div>
      {result?.loading && <LinearProgress />}
      <TableField
        columns={columns}
        onChange={() => {}}
        value={result?.items ?? []}
        visibleEdit={false}
        visibleAdd={false}
        visibleRemove={false}
        visibleActionsColumn={false}
        showToolbar={false}
        showRadioCond={() => false}
        showCheckboxCond={() => false}
      />
    </>
  );
}
